import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage             from './pages/LoginPage';
import Layout                from './components/common/Layout';

/* LoginPage carries the public landing page, so it is the one thing a
   stranger needs. Everything else is loaded when its route is first
   opened.

   All 33 pages used to be imported here, which meant a visitor who
   only wanted to read the homepage downloaded the whole application —
   3.4 MB of it, including the charting library used on two screens.
   Splitting them costs one extra request per page, once. */
const RegisterPage          = React.lazy(() => import('./pages/RegisterPage'));
const DashboardPage         = React.lazy(() => import('./pages/DashboardPage'));
const TripsPage             = React.lazy(() => import('./pages/TripsPage'));
const TripFormPage          = React.lazy(() => import('./pages/TripFormPage'));
const TripDetailPage        = React.lazy(() => import('./pages/TripDetailPage'));
/* LRPrintPage ab use nahi hota — uski jagah TripBillPage (menu ke saath). */
/* BiltyPrintPage ab use nahi hota — uski jagah BiltyPage (menu ke saath). */
/* InvoicePrintPage ka rasta hata diya gaya tha — kahin se khulta hi nahi tha. */
const InvoicesPage          = React.lazy(() => import('./pages/InvoicesPage'));
const InvoiceDetailPage     = React.lazy(() => import('./pages/InvoiceDetailPage'));
const ChallansPage         = React.lazy(() => import('./pages/ChallansPage'));
const ChallanDetailPage    = React.lazy(() => import('./pages/ChallanDetailPage'));
const OnlineLRPage          = React.lazy(() => import('./pages/OnlineLRPage'));
const SuperAdminPage        = React.lazy(() => import('./pages/SuperAdminPage'));
const EmailSettingsPage     = React.lazy(() => import('./pages/EmailSettingsPage'));
const PrintDesignPage       = React.lazy(() => import('./pages/PrintDesignPage'));
const BackupPage            = React.lazy(() => import('./pages/BackupPage'));
const MemoPage              = React.lazy(() => import('./pages/MemoPage'));
const MarketingPage         = React.lazy(() => import('./pages/MarketingPage'));
const BiltyPage             = React.lazy(() => import('./pages/BiltyPage'));
const TripBillPage          = React.lazy(() => import('./pages/TripBillPage'));
const RemindersPage         = React.lazy(() => import('./pages/RemindersPage'));
const RenewPage             = React.lazy(() => import('./pages/RenewPage'));
const PartiesPage           = React.lazy(() => import('./pages/PartiesPage'));
const PartyDetailPage       = React.lazy(() => import('./pages/PartyDetailPage'));
const TransportersPage      = React.lazy(() => import('./pages/TransportersPage'));
const TransporterDetailPage = React.lazy(() => import('./pages/TransporterDetailPage'));
const DriversPage           = React.lazy(() => import('./pages/DriversPage'));
const DriverDetailPage      = React.lazy(() => import('./pages/DriverDetailPage'));
const VehiclesPage          = React.lazy(() => import('./pages/VehiclesPage'));
const ExpensesListPage      = React.lazy(() => import('./pages/ExpensesListPage'));
const PaymentsPage          = React.lazy(() => import('./pages/PaymentsPage'));
const LedgerPage            = React.lazy(() => import('./pages/LedgerPage'));
const BanksPage             = React.lazy(() => import('./pages/BanksPage'));
const ReportsPage           = React.lazy(() => import('./pages/ReportsPage'));
const MoneyDuePage          = React.lazy(() => import('./pages/MoneyDuePage'));
const UsersPage             = React.lazy(() => import('./pages/UsersPage'));
const ProfilePage           = React.lazy(() => import('./pages/ProfilePage'));
const SupportCenterPage     = React.lazy(() => import('./pages/SupportCenterPage'));
const BlogListPage          = React.lazy(() => import('./pages/BlogPages').then(m => ({ default: m.BlogListPage })));
const BlogPostPage          = React.lazy(() => import('./pages/BlogPages').then(m => ({ default: m.BlogPostPage })));
const PrivacyPage           = React.lazy(() => import('./pages/PrivacyPage'));
const TermsPage             = React.lazy(() => import('./pages/TermsPage'));
const SupportPage           = React.lazy(() => import('./pages/SupportPage'));

/* Shown while a page's chunk is fetched. Deliberately plain — a
   spinner that appears for 200ms is more distracting than a blank. */
function PageLoading() {
  return (
    <div style={{ minHeight:'60vh', display:'flex', alignItems:'center',
      justifyContent:'center', color:'#9ca3af', fontSize:'13px' }}>
      Loading…
    </div>
  );
}

function Guard({ children }) {
  var { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f4f5f7' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:'44px', marginBottom:'10px' }}>🚛</div>
        <p style={{ color:'#16a34a', fontWeight:'700', fontSize:'15px', margin:0 }}>TransportMaster</p>
        <p style={{ color:'#9ca3af', fontSize:'12px', marginTop:'4px' }}>Loading...</p>
      </div>
    </div>
  );
  return user ? children : <Navigate to="/login" replace/>;
}

function AppRoutes() {
  var { user, loading } = useAuth();

  /* The session check blocked every page, including the ones that do
     not need it.

     Blog posts, the legal pages and support are public — no user, no
     permissions, nothing to wait for. But this gate held them behind a
     "Loading…" screen while /api/auth/me answered, and Google renders
     a page and photographs it within a second or two. It kept seeing
     that screen instead of the article and filed every post as a soft
     404, which is exactly what Search Console was reporting.

     The wait still applies to the app, where reading `user` too early
     would flash the landing page over someone's dashboard. */
  var publicPath = /^\/(hi\/)?(blog|privacy|terms|support|help|pricing|bilty-software|bilty-format|transport-billing-software|trip-management-software|party-ledger-software|excel-se-transport-software|lorry-receipt-lr|truck-types-and-capacity|freight-rate-calculation|delete-account|transport-software-in-[a-z-]+)(\/|$)/.test(
    typeof window !== 'undefined' ? window.location.pathname : '/');

  if (loading && !publicPath) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center',
      justifyContent:'center', background:'#f4f5f7' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:'44px', marginBottom:'10px' }}>🚛</div>
        <p style={{ color:'#16a34a', fontWeight:'700', fontSize:'15px', margin:0 }}>TransportMaster</p>
        <p style={{ color:'#9ca3af', fontSize:'12px', marginTop:'4px' }}>Loading…</p>
      </div>
    </div>
  );

  return (
    /* Every lazy page needs a Suspense above it, or React throws the
       moment one is first opened. One boundary here covers all of them. */
    <Suspense fallback={<PageLoading/>}>
    <Routes>
      {/* Public routes - no auth needed */}

      <Route path="/login"      element={user ? <Navigate to="/dashboard" replace/> : <LoginPage/>}/>
      <Route path="/register"   element={user ? <Navigate to="/dashboard" replace/> : <RegisterPage/>}/>
      <Route path="/lr/*"  element={<OnlineLRPage/>}/>
      <Route path="/super-admin" element={<SuperAdminPage/>}/>
      {/* Plan khatam wala page — Layout ke bahar, taaki menu na dikhe */}
      <Route path="/renew" element={<Guard><RenewPage/></Guard>}/>
      <Route path="/bilty/*" element={<OnlineLRPage/>}/>

      {/* Legal + support - public, login ki zaroorat nahi */}
      <Route path="/privacy" element={<PrivacyPage/>}/>
      <Route path="/terms"   element={<TermsPage/>}/>
      <Route path="/support" element={<SupportPage/>}/>

      {/* SEO ke page — content backend se (seoPages.js), daam plans table se.
         Naya page wahan jodo, phir yahan uska slug bhi jod dena. */}
      <Route path="/pricing"                      element={<MarketingPage/>}/>
      <Route path="/bilty-software"               element={<MarketingPage/>}/>
      <Route path="/bilty-format"                 element={<MarketingPage/>}/>
      <Route path="/transport-billing-software"   element={<MarketingPage/>}/>
      <Route path="/trip-management-software"     element={<MarketingPage/>}/>
      <Route path="/party-ledger-software"        element={<MarketingPage/>}/>
      <Route path="/excel-se-transport-software"  element={<MarketingPage/>}/>
      <Route path="/lorry-receipt-lr"             element={<MarketingPage/>}/>
      <Route path="/truck-types-and-capacity"     element={<MarketingPage/>}/>
      <Route path="/freight-rate-calculation"     element={<MarketingPage/>}/>
      <Route path="/delete-account"               element={<MarketingPage/>}/>
      {/* Hinglish roop — wahi page, /hi/ ke saath */}
      <Route path="/hi/:slug"                     element={<MarketingPage/>}/>
      {/* Shehar ke page. Har ek ka apna raasta likhna padta hai —
          React Router v6 me "/transport-software-in-:city" jaisa raasta
          chalta nahi (likha hua text aur :param ek hi hisse me nahi aa sakte),
          aur aise me page ghar par chala jaata hai.
          NAYA SHEHAR: backend/seoContentCities.js me jodne ke baad yahan bhi
          ek line jodna, warna user ko ghar ka page milega. */}
      <Route path="/transport-software-in-delhi"     element={<MarketingPage/>}/>
      <Route path="/transport-software-in-mumbai"    element={<MarketingPage/>}/>
      <Route path="/transport-software-in-ahmedabad" element={<MarketingPage/>}/>
      <Route path="/transport-software-in-jaipur"    element={<MarketingPage/>}/>
      <Route path="/transport-software-in-indore"    element={<MarketingPage/>}/>
      <Route path="/transport-software-in-ludhiana"  element={<MarketingPage/>}/>

      {/* Blog — public, SEO ke liye */}
      <Route path="/blog"       element={<BlogListPage/>}/>
      <Route path="/blog/:slug" element={<BlogPostPage/>}/>

      {/* Print routes - auth needed but no sidebar */}
      {/* Purana trip bill ka rasta ab naye (menu wale) page par */}
      <Route path="/trips/:id/lr-print"      element={<Guard><TripBillRedirect/></Guard>}/>
      {/* Purana bilty print page ab naye (menu wale) page par le jata hai */}
      <Route path="/trips/:id/bilty-print"   element={<Guard><BiltyPrintRedirect/></Guard>}/>
      {/* purana /trips/:id/invoice-print rasta hata diya — kahin se khulta hi nahi tha */}

      {/* Main app.

          "/" used to be wrapped in Guard, so a visitor who was not
          signed in got bounced to /login. The landing page still
          appeared — it lives inside LoginPage — but the URL became
          /login, and that is the URL Google indexed. A login form as
          the homepage, a canonical pointing at a URL that instantly
          redirects, and 44 blog articles linking to a password box.

          The guard belongs on the dashboard, not on the address. A
          stranger at "/" now gets the landing page with no redirect;
          a signed-in user gets the app exactly as before. */}
      {/* "/" is the landing page and nothing else.

          It used to be the app's home as well: a signed-in visitor
          typing transportmaster.in landed on their dashboard, and the
          marketing page had no address of its own. The two are
          different pages for different people, so they get different
          URLs — the app lives at /dashboard now.

          Googlebot is never signed in, so it always sees the landing
          page here, with no redirect. */}
      <Route path="/" element={user ? <Navigate to="/dashboard" replace/> : <LoginPage/>}/>

      {/* The app. A pathless layout route, so every page below keeps
          its own top-level URL. */}
      <Route element={<Guard><Layout/></Guard>}>
        <Route path="dashboard" element={<DashboardPage/>}/>
        <Route path="trips"                element={<TripsPage/>}/>
        <Route path="trips/new"            element={<TripFormPage/>}/>
        <Route path="trips/:id"            element={<TripDetailPage/>}/>
        <Route path="trips/:id/edit"       element={<TripFormPage/>}/>
        <Route path="trips/:id/memo"       element={<MemoPage/>}/>
        {/* Bilty bhi memo ki tarah app ke andar — menu aur header ke saath */}
        <Route path="trips/:id/bilty"      element={<BiltyPage/>}/>
        {/* Trip bill bhi menu ke saath — memo/bilty ki tarah */}
        <Route path="trips/:id/bill"       element={<TripBillPage/>}/>
        <Route path="parties"              element={<PartiesPage/>}/>
        <Route path="parties/:id"          element={<PartyDetailPage/>}/>
        <Route path="transporters"         element={<TransportersPage/>}/>
        <Route path="transporters/:id"     element={<TransporterDetailPage/>}/>
        <Route path="drivers"              element={<DriversPage/>}/>
        <Route path="drivers/:id"          element={<DriverDetailPage/>}/>
        <Route path="vehicles"             element={<VehiclesPage/>}/>
        <Route path="expenses-list"        element={<ExpensesListPage/>}/>
        <Route path="payments"             element={<PaymentsPage/>}/>
        <Route path="ledger"               element={<LedgerPage/>}/>
        <Route path="banks"                element={<BanksPage/>}/>
        <Route path="money-due"            element={<MoneyDuePage/>}/>
        <Route path="reports"              element={<ReportsPage/>}/>
        <Route path="users"                element={<UsersPage/>}/>
        <Route path="settings/email"        element={<EmailSettingsPage/>}/>
        <Route path="settings/print-design" element={<PrintDesignPage/>}/>
        <Route path="settings/backup"       element={<BackupPage/>}/>
        <Route path="settings/daily-summary" element={<RemindersPage/>}/>
        <Route path="profile"              element={<ProfilePage/>}/>
        <Route path="help"                 element={<SupportCenterPage/>}/>
        <Route path="invoices"             element={<InvoicesPage/>}/>
        <Route path="invoices/:id"         element={<InvoiceDetailPage/>}/>
        <Route path="challans"             element={<ChallansPage/>}/>
        <Route path="challans/:id"         element={<ChallanDetailPage/>}/>
      </Route>
      {/* An unknown URL sends a stranger to the landing page and a
          signed-in user back to their dashboard. */}
      <Route path="*" element={<Navigate to={user ? '/dashboard' : '/'} replace/>}/>
    </Routes>
    </Suspense>
  );
}

/* Purana /trips/:id/bilty-print ab naye page par — ek hi jagah rahe. */
/* Purana /trips/:id/lr-print ab naye bill page par. */
function TripBillRedirect() {
  var p = useParams();
  return <Navigate to={'/trips/' + p.id + '/bill'} replace/>;
}

function BiltyPrintRedirect() {
  var p = useParams();
  return <Navigate to={'/trips/' + p.id + '/bilty'} replace/>;
}
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" toastOptions={{ duration:3000, style:{ fontSize:'13px', fontWeight:'600', borderRadius:'9px' } }}/>
        <AppRoutes/>
      </AuthProvider>
    </BrowserRouter>
  );
}
